import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthCtx = createContext(null);

const formatDetail = (d) => {
  if (d == null) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (d && typeof d.msg === "string") return d.msg;
  return String(d);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const check = async () => {
    // CRITICAL: skip /me while OAuth callback still needs to exchange session_id
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(r.data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { check(); }, []);

  const loginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const loginEmail = async (email, password) => {
    try {
      const r = await axios.post(`${API}/auth/login`,
        { email, password }, { withCredentials: true });
      setUser(r.data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name) => {
    try {
      const r = await axios.post(`${API}/auth/register`,
        { email, password, name }, { withCredentials: true });
      setUser(r.data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatDetail(e.response?.data?.detail) || e.message };
    }
  };

  const forgotPassword = async (email) => {
    try {
      await axios.post(`${API}/auth/forgot-password`, { email }, { withCredentials: true });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatDetail(e.response?.data?.detail) || e.message };
    }
  };

  const resetPassword = async (token, password) => {
    try {
      const r = await axios.post(`${API}/auth/reset-password`,
        { token, password }, { withCredentials: true });
      setUser(r.data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (e) { console.debug("logout request failed:", e); }
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{
      user, loading,
      login: loginGoogle,            // legacy alias — Header uses this for the Google flow
      loginGoogle, loginEmail, register, forgotPassword, resetPassword,
      logout, refresh: check,
    }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
